<template>
  <div
    role="dialog"
    aria-describedby="alert-history-description"
    aria-labelledby="alert-history-title"
    data-state="open"
    class="relative p-6 detail-history-alert"
  >
    <div class="flex flex-col space-y-1.5 text-center sm:text-left">
      <h2
        id="alert-history-title"
        class="text-lg font-semibold leading-none tracking-tight"
      >
        {{ headerTitle }}
      </h2>
      <p id="alert-history-description" class="text-sm text-muted-foreground">
        Complete history and notification log for this alert
      </p>
    </div>

    <div class="space-y-6 py-4">
      <el-descriptions class="mb-4" :column="2" border size="small" title="">
        <el-descriptions-item
          :span="2"
          label-class-name="font-semibold text-base"
        >
          {{ item.rule_name || item.ruleName || "--" }}
        </el-descriptions-item>
        <el-descriptions-item label="Device">
          {{ item.params?.cameraName || "--" }}
        </el-descriptions-item>
        <el-descriptions-item label="Type">
          {{ item.type || "--" }}
        </el-descriptions-item>
        <el-descriptions-item label="Status">
          <span class="badge" :class="getStatusBadgeClass(item.status)">
            {{ item.status || "--" }}
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="Feedback State">
          {{ formatFeedbackState(item.feedback_state) }}
        </el-descriptions-item>
        <el-descriptions-item label="Notification ID">
          {{ item.notification_id || "--" }}
        </el-descriptions-item>
        <el-descriptions-item label="Timestamp">
          {{ formatTimestamp(item.timestamp) }}
        </el-descriptions-item>
        <el-descriptions-item label="Trigger Type">
          {{ item.params?.triggerType || "--" }}
        </el-descriptions-item>
        <el-descriptions-item label="Camera ID">
          {{ item.params?.cameraId || "--" }}
        </el-descriptions-item>
      </el-descriptions>
      <h4 class="font-semibold text-base">Params:</h4>
      <el-input
        :value="JSON.stringify(item.params, null, 2)"
        type="textarea"
        :rows="6"
        readonly
      />
      <section>
        <div class="flex items-center justify-between mb-3">
          <div>
            <h4 class="font-semibold text-base">Notification History</h4>
            <p class="text-sm text-muted-foreground">
              Showing all notifications sent for this rule/event
            </p>
          </div>
          <span class="text-sm text-muted-foreground">
            {{ tableCtrl.state.pagination.total }} record{{
              tableCtrl.state.pagination.total === 1 ? "" : "s"
            }}
          </span>
        </div>

        <el-table
          v-loading="tableCtrl.state.loading"
          element-loading-background="rgba(24, 33, 48, 0.8)"
          :data="tableCtrl.state.tableData"
          style="width: 100%"
          :resizable="false"
        >
          <el-table-column
            prop="recipient_name"
            label="Recipient"
            min-width="160"
          >
            <template #default="{ row }">
              <div class="flex flex-col leading-tight">
                <span class="font-medium">{{
                  row.recipient_name || "--"
                }}</span>
                <span class="text-xs text-muted-foreground">
                  {{ row.recipient_id ? "ID: " + row.recipient_id : "--" }}
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="channel" label="Channel" width="120">
            <template #default="{ row }">
              {{ row.channel || "--" }}
            </template>
          </el-table-column>
          <el-table-column
            prop="delivery_status"
            label="Delivery Status"
            width="140"
          >
            <template #default="{ row }">
              <span>{{ formatDeliveryStatus(row.delivery_status) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="escalation_level"
            label="Escalation"
            width="100"
          >
            <template #default="{ row }">
              {{ row.escalation_level ?? "--" }}
            </template>
          </el-table-column>
          <el-table-column
            prop="error_msg"
            label="Error Message"
            min-width="180"
          >
            <template #default="{ row }">
              <span class="text-xs text-red-500">{{
                row.error_msg || "--"
              }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="sent_at" label="Sent At" width="190">
            <template #default="{ row }">
              {{ formatTimestamp(row.sent_at) }}
            </template>
          </el-table-column>
        </el-table>

        <div class="flex justify-between items-center mt-4">
          <el-pagination
            @size-change="(val) => tableCtrl.setPageSize(val)"
            @current-change="(val) => tableCtrl.setCurrentPage(val)"
            :current-page="tableCtrl.state.pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="tableCtrl.state.pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="tableCtrl.state.pagination.total"
          />
          <el-button type="button" @click="$emit('cancel')"> Close </el-button>
        </div>
      </section>
    </div>
    <button
      @click="$emit('cancel')"
      class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
    >
      <i class="icon-x size-4" />
      <span class="sr-only">Close</span>
    </button>
  </div>
</template>

<script>
import { getNotificationHistoryV2 } from "@/api";
import { TableController } from "@/controllers/TableController";

export default {
  name: "DetailHistoryAlert",
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      tableData: [],
      loading: false,
      tableCtrl: new TableController({
        loadMethod: this.fetchData,
        autoLoad: true,
        isClientPaged: false,
        pageSize: 10,
        keyField: "recipient_channel_id",
        totalField: "total",
        listField:'historyList',
        queryParameters: {
          notification_id: this.item.notification_id || null,
        },
      }),
    };
  },
  computed: {
    headerTitle() {
      return this.item.ruleName || this.item.assetName || "Alert Details";
    },
  },
  methods: {
    getStatusBadgeClass(status) {
      switch (status) {
        case "SENT":
          return "badge-success";
        case "FAILED":
          return "badge-danger";
        case "PENDING":
          return "badge-warning";
        default:
          return "badge-muted";
      }
    },
    formatTimestamp(value) {
      if (!value) return "--";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "--";
      return date.toLocaleString();
    },
    formatDeliveryStatus(status) {
      if (status === 1) return "Delivered";
      if (status === 0) return "Pending";
      if (status === 2) return "Failed";
      return "--";
    },
    formatFeedbackState(state) {
      if (state === 0) return "Pending";
      if (state === 1) return "Acknowledged";
      if (state === 2) return "Rejected";
      if (state === undefined || state === null) return "--";
      return String(state);
    },
    async fetchData(params) {
      const res = await getNotificationHistoryV2(params);
      return res;
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
}

.divider {
  height: 1px;
  width: 100%;
  background: rgba(148, 163, 184, 0.2);
}

.message-preview {
  max-height: 240px;
  overflow: auto;
  white-space: pre-wrap;
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 0.5rem;
  padding: 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
}

.badge {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 0.125rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid transparent;
}

.badge-low {
  background: rgba(34, 197, 94, 0.15);
  color: #4ade80;
  border-color: rgba(34, 197, 94, 0.4);
}

.badge-medium {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
  border-color: rgba(251, 191, 36, 0.4);
}

.badge-high {
  background: rgba(248, 113, 113, 0.15);
  color: #f87171;
  border-color: rgba(248, 113, 113, 0.4);
}

.badge-success {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
  border-color: rgba(34, 197, 94, 0.4);
}

.badge-warning {
  background: rgba(251, 191, 36, 0.15);
  color: #b45309;
  border-color: rgba(251, 191, 36, 0.4);
}

.badge-danger {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
  border-color: rgba(239, 68, 68, 0.4);
}

.badge-muted {
  background: rgba(148, 163, 184, 0.15);
  color: #94a3b8;
  border-color: rgba(148, 163, 184, 0.3);
}
</style>
